import React, { FC, useState, useCallback, useMemo, useEffect } from "react";
import MatchingOptionIcon from "@/components/icons/exerciseV2/MatchingOptionIcon";
import { Button } from "@/components/ui/Button";
import { useDrag, useDrop } from "react-dnd";
import GrammarExerciseResult from "./GrammarExerciseResult";
import { ExerciseMatchingOptionsItem, ExerciseOptionTypes, Submission } from "@/types";
import { calculateMaxWidth } from "@/utils/exercises";

interface WordOption {
  id: number;
  text: string;
  word: string;
  correctAnswer: string;
  isCorrect?: boolean;
  order: number;
}

interface SentenceBuilderProps {
  wordOptions: WordOption[];
  isCorrected: boolean;
  onCorrectAnswersCount: (count: number) => void;
  onDroppedWordsChange: (droppedWords: WordOption[]) => void;
}

/* eslint-disable react-refresh/only-export-components */
const ItemType = {
  WORD_OPTION: "wordOption",
};

const shuffleArray = (array: string[]) => {
  const shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
};

/* eslint-disable react-refresh/only-export-components */
const DraggableWord: FC<{
  word: WordOption;
  index: number;
  moveWord: (dragIndex: number, hoverIndex: number) => void;
  maxWidth: number;
}> = React.memo(({ word, index, moveWord, maxWidth }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemType.WORD_OPTION,
    item: () => ({ id: word.id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemType.WORD_OPTION,
    hover(item: { id: number, index: number }) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      moveWord(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`flex justify-start items-center w-full gap-1 h-[25px] p-2 font-semibold text-[10px] text-white whitespace-nowrap bg-brand-secondary rounded-md cursor-grab ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
      style={{ width: `${maxWidth}px`, height: '25px', minWidth: `121px` }}
    >
      <svg
        width="7"
        height="17"
        viewBox="0 0 7 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 13.5L3.5 16M3.5 16L6 13.5M3.5 16V11M6 3.5L3.5 1M3.5 1L1 3.5M3.5 1V6"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {word.word}
    </div>
  );
});

const SentenceBuilder: FC<SentenceBuilderProps> = ({ 
  wordOptions, 
  isCorrected, 
  onCorrectAnswersCount, 
  onDroppedWordsChange 
}) => {
  const [words, setWords] = useState<WordOption[]>(wordOptions);
  const maxWidth = useMemo(() => calculateMaxWidth(wordOptions), [wordOptions]);

  // Synchronize words with incoming wordOptions
  useEffect(() => {
    setWords(wordOptions);
  }, [wordOptions]);

  const moveWord = useCallback((dragIndex: number, hoverIndex: number) => {
    setWords((prevWords) => {
      const newWords = [...prevWords];
      const temp = newWords[dragIndex];
      newWords[dragIndex] = newWords[hoverIndex];
      newWords[hoverIndex] = temp;
      onDroppedWordsChange(newWords);
      return newWords;
    });
  }, [onDroppedWordsChange]);

  const handleCorrection = useCallback(() => {
    const correctAnswersMap = words.reduce<{ [key: number]: string }>(
      (map, option) => {
        map[option.id] = option.correctAnswer;
        return map;
      },
      {},
    );

    const correctedOptions = words.map((option) => {
      const correctAnswer = correctAnswersMap[option.id];
      const isCorrect = option.word === correctAnswer;
      return { ...option, isCorrect };
    });

    const correctCount = correctedOptions.filter(
      (option) => option.isCorrect,
    ).length;
    onCorrectAnswersCount(correctCount);
  }, [words, onCorrectAnswersCount]);

  useEffect(() => {
    if (isCorrected) {
      handleCorrection();
    }
  }, [isCorrected, handleCorrection]);

  return (
    <div className="flex gap-1.5 w-full">
      <div className="flex flex-col gap-1.5 mt-1.5">
        {words.map((option, index) => (
          <div key={option.id} className="flex-1">
            <DraggableWord 
              word={option} 
              index={index} 
              moveWord={moveWord} 
              maxWidth={maxWidth} 
            />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-1.5 mt-1.5 w-full">
        {wordOptions.map((option) => (
          <div
            key={option.id}
            className={`grow flex-3 items-center pl-2 font-medium text-[8px] bg-light-400 rounded-md text-black`}
          >
            {option.text}
          </div>
        ))}
      </div>
    </div>
  );
};

const MatchingOptionExercise: FC<{ data: ExerciseMatchingOptionsItem[] }> = ({ data }) => {
  const [wordOptions, setWordOptions] = useState<WordOption[]>([]);
  const [isCorrected, setIsCorrected] = useState(false);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [showResults, setShowResults] = useState(false);

  // Prepare word options when data changes
  useEffect(() => {
    if (data && data.length > 0) {
      const preparedOptions = data.map((item, index) => ({
        id: item.id,
        text: item.statement,
        correctAnswer: item.correct_answer,
        order: index,
        word: '', // Placeholder
      }));

      const wordsOnly = preparedOptions.map((option) => option.correctAnswer);
      const shuffledWords = shuffleArray(wordsOnly);

      const finalWordOptions = preparedOptions.map((option, index) => ({
        ...option,
        word: shuffledWords[index],
      }));

      setWordOptions(finalWordOptions);
    }
  }, [data]);

  const startCorrection = useCallback(() => {
    setIsCorrected(true);
  }, []);

  const handleCorrectAnswersCount = useCallback((count: number) => {
    setCorrectAnswersCount(count);
  }, []);

  const handleDroppedWordsChange = useCallback((droppedWords: WordOption[]) => {
    // Optional: Add any logic for tracking dropped words
    console.warn("Dropped words:", droppedWords);
  }, []);

  const handleShowMyScore = useCallback(() => {
    setShowResults(true);
  }, []);

  // Prevent unnecessary body style changes
  useEffect(() => {
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  if (showResults) {
    return (
      <GrammarExerciseResult
        onClose={() => setShowResults(false)}
        data={{
          exerciseType: ExerciseOptionTypes.MatchingOption,
          score: correctAnswersCount,
          total: data.length,
          submissionData: wordOptions.map((option) => ({
            questionId: option.id,
            textAnswer: option.word,
            isCorrect: option.isCorrect,
          })) as Submission[],
        }}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 p-3 bg-white rounded-lg border border-brand-secondary border-solid">
        <header className="flex gap-5 justify-between w-full">
          <div className="flex gap-2 justify-between items-center text-xl leading-2 text-black">
            <MatchingOptionIcon className="md:w-4 md:h-4" />
            <div className="w-full">
              <p className="text-[12px] font-semibold ">Matching option</p>
              <p className="text-[10px] leading-[10px] font-normal text-black">
              Reorder the words by moving them up and down until they are positioned next to the correct sentence.
              </p>
            </div>
          </div>
        </header>
        {wordOptions.length > 0 ? (
          <div>
            <SentenceBuilder
              wordOptions={wordOptions}
              isCorrected={isCorrected}
              onCorrectAnswersCount={handleCorrectAnswersCount}
              onDroppedWordsChange={handleDroppedWordsChange}
            />
          </div>
        ) : (
          <div className="text-center text-gray-500">Loading options...</div>
        )}
      </div>
      {!isCorrected && wordOptions.length > 0 && (
        <Button
          className="mx-auto mt-4 text-[10px] rounded-[10px] w-full border-black bg-white hover:bg-white hover:text-black"
          variant={"outline"}
          disabled={isCorrected}
          onClick={startCorrection}
        >
          Correct now
        </Button>
      )}
      {isCorrected && (
        <Button
          variant={"default"}
          className="mx-auto mt-4 text-[10px] hover:bg-brand-secondary w-full rounded-[10px]"
          onClick={handleShowMyScore}
        >
          My Score ⭐
        </Button>
      )}
    </>
  );
};

export default React.memo(MatchingOptionExercise);